import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Users, ChevronLeft, ChevronRight, X, Package, Mail, Phone, Briefcase, MapPin, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface Department {
  id: string;
  name: string;
  code: string;
  branch?: { id: string; name: string };
  branchId?: string;
  departmentHead?: { 
    id: string; 
    firstName: string; 
    lastName: string; 
    email?: string;
    employeeId: string;
  };
  departmentHeadId?: string;
  _count?: { employees: number };
  createdAt: string;
}

interface Branch {
  id: string;
  name: string;
}

interface DepartmentFormData {
  name: string;
  code: string;
  branchId: string;
  departmentHeadId: string;
}

interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  position?: string;
  jobTitle?: string;
  isActive: boolean;
  department?: { id: string; name: string };
  branch?: { id: string; name: string };
  _count?: { assets: number };
  assets?: Asset[];
}

interface Asset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  status: string;
  condition?: string;
  category?: { id: string; name: string };
  brand?: { id: string; name: string };
  model?: { id: string; name: string };
}

const initialFormData: DepartmentFormData = {
  name: '',
  code: '',
  branchId: '',
  departmentHeadId: '',
};

export default function DepartmentsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filterBranchId, setFilterBranchId] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>(initialFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [headSearch, setHeadSearch] = useState('');
  
  // Employee list modal state
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  
  // Employee detail modal state
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Fetch branches for dropdown
  const { data: branchesData } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data || [];
    },
  });
  const branches: Branch[] = branchesData || [];

  // Fetch employees for department head dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['employees-for-dept-head'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=500&status=ACTIVE');
      return response.data.data || [];
    },
    enabled: showModal,
  });
  const employees = employeesData || [];

  // Fetch departments
  const { data, isLoading } = useQuery({
    queryKey: ['departments', { page, search, filterBranchId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (filterBranchId) params.append('branchId', filterBranchId);
      const response = await api.get(`/departments?${params.toString()}`);
      return response.data;
    },
  });

  const departments: Department[] = data?.data || [];
  const pagination = data?.pagination;

  // Fetch employees for selected department
  const { data: departmentEmployeesData, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['department-employees', selectedDepartment?.id, employeeSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('departmentId', selectedDepartment!.id);
      params.append('limit', '100');
      if (employeeSearch) params.append('search', employeeSearch);
      const response = await api.get(`/employees?${params.toString()}`);
      return response.data;
    },
    enabled: showEmployeesModal && !!selectedDepartment,
  });
  const departmentEmployees: Employee[] = departmentEmployeesData?.data || [];

  // Fetch employee details with assets
  const { data: employeeDetailData, isLoading: isLoadingEmployeeDetail } = useQuery({
    queryKey: ['employee-detail', selectedEmployee?.id],
    queryFn: async () => {
      const response = await api.get(`/employees/${selectedEmployee!.id}`);
      return response.data.data;
    },
    enabled: showEmployeeDetail && !!selectedEmployee,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: DepartmentFormData) => {
      return api.post('/departments', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      closeModal();
      toast({ title: 'Success', description: 'Department created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create department', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DepartmentFormData }) => {
      return api.put(`/departments/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      closeModal();
      toast({ title: 'Success', description: 'Department updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update department', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/departments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Department deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete department', variant: 'destructive' });
    },
  });

  const openCreateModal = () => {
    setEditingDepartment(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (department: Department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      code: department.code,
      branchId: department.branchId || department.branch?.id || '',
      departmentHeadId: department.departmentHeadId || department.departmentHead?.id || '',
    });
    setHeadSearch('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDepartment(null);
    setFormData(initialFormData);
    setHeadSearch('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingDepartment) {
      updateMutation.mutate({ id: editingDepartment.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const confirmDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const openEmployeesModal = (department: Department) => {
    setSelectedDepartment(department);
    setEmployeeSearch('');
    setShowEmployeesModal(true);
  };

  const closeEmployeesModal = () => {
    setShowEmployeesModal(false);
    setSelectedDepartment(null);
    setEmployeeSearch('');
  };

  const openEmployeeDetail = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeDetail(true);
  };

  const backToEmployeesList = () => {
    setShowEmployeeDetail(false);
    setSelectedEmployee(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Departments</h1>
          <p className="text-muted-foreground">Manage organization departments</p>
        </div>
        <PermissionGate permission="departments.create">
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Add Department
          </Button>
        </PermissionGate>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search departments..."
                className="pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[180px]"
              value={filterBranchId}
              onChange={(e) => {
                setFilterBranchId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No departments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Code</th>
                    <th className="text-left p-4 font-medium">Department Head</th>
                    <th className="text-left p-4 font-medium">Branch</th>
                    <th className="text-left p-4 font-medium">Employees</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {departments.map((department) => (
                    <tr key={department.id} className="hover:bg-muted/50">
                      <td className="p-4 font-medium">{department.name}</td>
                      <td className="p-4">
                        <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                          {department.code}
                        </span>
                      </td>
                      <td className="p-4">
                        {department.departmentHead ? (
                          <div>
                            <p className="font-medium">{department.departmentHead.firstName} {department.departmentHead.lastName}</p>
                            <p className="text-xs text-muted-foreground">{department.departmentHead.email || department.departmentHead.employeeId}</p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground">{department.branch?.name || '-'}</td>
                      <td className="p-4">
                        <button
                          onClick={() => openEmployeesModal(department)}
                          className="inline-flex items-center gap-1 hover:text-primary hover:bg-primary/10 px-2 py-1 rounded-md transition-colors cursor-pointer"
                          title={`View ${department._count?.employees || 0} employees in ${department.name}`}
                        >
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{department._count?.employees || 0}</span>
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <PermissionGate permission="departments.update">
                            <Button variant="ghost" size="icon" onClick={() => openEditModal(department)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="departments.delete">
                            <Button variant="ghost" size="icon" onClick={() => confirmDelete(department.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingDepartment ? 'Edit Department' : 'Create Department'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="code">Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      placeholder="e.g., IT, HR, FIN"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="branchId">Branch</Label>
                  <select
                    id="branchId"
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={formData.branchId}
                    onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Department Head</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search employees..."
                      className="pl-10"
                      value={headSearch}
                      onChange={(e) => setHeadSearch(e.target.value)}
                    />
                  </div>
                  <div className="mt-2 max-h-32 overflow-y-auto border rounded-md">
                    {/* No department head option */}
                    <div
                      className={`px-3 py-2 cursor-pointer hover:bg-muted ${!formData.departmentHeadId ? 'bg-primary/10' : ''}`}
                      onClick={() => setFormData({ ...formData, departmentHeadId: '' })}
                    >
                      <p className="text-sm text-muted-foreground">No department head</p>
                    </div>
                    {employees
                      .filter((emp: any) => {
                        if (!headSearch) return true;
                        const search = headSearch.toLowerCase();
                        return (
                          emp.firstName?.toLowerCase().includes(search) ||
                          emp.lastName?.toLowerCase().includes(search) ||
                          emp.email?.toLowerCase().includes(search) ||
                          emp.employeeId?.toLowerCase().includes(search)
                        );
                      })
                      .slice(0, 20)
                      .map((emp: any) => (
                        <div
                          key={emp.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-muted ${formData.departmentHeadId === emp.id ? 'bg-primary/10' : ''}`}
                          onClick={() => setFormData({ ...formData, departmentHeadId: emp.id })}
                        >
                          <p className="text-sm font-medium">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-muted-foreground">{emp.email || emp.employeeId}</p>
                        </div>
                      ))}
                  </div>
                  {formData.departmentHeadId && (
                    <p className="mt-2 text-sm text-primary">
                      Selected: {employees.find((e: any) => e.id === formData.departmentHeadId)?.firstName} {employees.find((e: any) => e.id === formData.departmentHeadId)?.lastName}
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                    {editingDepartment ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this department? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deletingId && deleteMutation.mutate(deletingId)}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Department Employees Modal */}
      {showEmployeesModal && selectedDepartment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <CardContent className="p-6 flex flex-col h-full">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold">
                    {showEmployeeDetail && selectedEmployee ? (
                      <span className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={backToEmployeesList} className="h-8 w-8">
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </span>
                    ) : (
                      `Employees in ${selectedDepartment.name}`
                    )}
                  </h2>
                  {!showEmployeeDetail && (
                    <p className="text-sm text-muted-foreground">
                      {departmentEmployees.length} employee{departmentEmployees.length !== 1 ? 's' : ''} found
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={closeEmployeesModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Employee Detail View */}
              {showEmployeeDetail && selectedEmployee ? (
                <div className="flex-1 overflow-y-auto">
                  {isLoadingEmployeeDetail ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : employeeDetailData ? (
                    <div className="space-y-6">
                      {/* Employee Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-primary" />
                            Employee Information
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Employee ID</span>
                              <span className="font-mono">{employeeDetailData.employeeId}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Name</span>
                              <span>{employeeDetailData.firstName} {employeeDetailData.lastName}</span>
                            </div>
                            {employeeDetailData.email && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Email</span>
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {employeeDetailData.email}
                                </span>
                              </div>
                            )}
                            {employeeDetailData.phone && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Phone</span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {employeeDetailData.phone}
                                </span>
                              </div>
                            )}
                            {(employeeDetailData.position || employeeDetailData.jobTitle) && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Position</span>
                                <span>{employeeDetailData.position || employeeDetailData.jobTitle}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Status</span>
                              <span className={cn(
                                "px-2 py-0.5 rounded-full text-xs font-medium",
                                employeeDetailData.isActive 
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                              )}>
                                {employeeDetailData.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-primary" />
                            Location
                          </h3>
                          <div className="space-y-2 text-sm">
                            {employeeDetailData.department && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Department</span>
                                <span>{employeeDetailData.department.name}</span>
                              </div>
                            )}
                            {employeeDetailData.branch && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Branch</span>
                                <span>{employeeDetailData.branch.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Assets Section */}
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Package className="w-5 h-5 text-primary" />
                          Assigned Assets ({employeeDetailData.assets?.length || 0})
                        </h3>
                        
                        {employeeDetailData.assets && employeeDetailData.assets.length > 0 ? (
                          <div className="border rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-3 text-sm font-medium">Asset</th>
                                  <th className="text-left p-3 text-sm font-medium">Category</th>
                                  <th className="text-left p-3 text-sm font-medium">Serial Number</th>
                                  <th className="text-left p-3 text-sm font-medium">Status</th>
                                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {employeeDetailData.assets.map((asset: Asset) => (
                                  <tr key={asset.id} className="hover:bg-muted/50">
                                    <td className="p-3">
                                      <div>
                                        <p className="font-medium">{asset.name}</p>
                                        <p className="text-xs text-muted-foreground font-mono">{asset.assetTag}</p>
                                      </div>
                                    </td>
                                    <td className="p-3 text-sm">{asset.category?.name || '-'}</td>
                                    <td className="p-3 text-sm font-mono">{asset.serialNumber || '-'}</td>
                                    <td className="p-3">
                                      <span className={cn(
                                        "px-2 py-0.5 rounded-full text-xs font-medium",
                                        asset.status === 'AVAILABLE' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
                                        asset.status === 'ASSIGNED' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
                                        asset.status === 'IN_MAINTENANCE' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
                                        asset.status === 'RETIRED' && "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                                      )}>
                                        {asset.status?.replace('_', ' ')}
                                      </span>
                                    </td>
                                    <td className="p-3 text-right">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => navigate(`/assets/${asset.id}`)}
                                      >
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-8 border rounded-lg bg-muted/20">
                            <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-2" />
                            <p className="text-muted-foreground">No assets assigned to this employee</p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <Button variant="outline" onClick={backToEmployeesList}>
                          Back to List
                        </Button>
                        <Button onClick={() => navigate(`/employees/${selectedEmployee.id}`)}>
                          View Full Profile
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">Employee not found</p>
                    </div>
                  )}
                </div>
              ) : (
                /* Employee List View */
                <>
                  {/* Search */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search employees..."
                      className="pl-10"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                    />
                  </div>

                  {/* Employee List */}
                  <div className="flex-1 overflow-y-auto">
                    {isLoadingEmployees ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : departmentEmployees.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-muted-foreground">No employees found in this department</p>
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 text-sm font-medium">Employee</th>
                              <th className="text-left p-3 text-sm font-medium">Position</th>
                              <th className="text-left p-3 text-sm font-medium">Contact</th>
                              <th className="text-left p-3 text-sm font-medium">Assets</th>
                              <th className="text-left p-3 text-sm font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {departmentEmployees.map((employee) => (
                              <tr 
                                key={employee.id} 
                                className="hover:bg-muted/50 cursor-pointer"
                                onClick={() => openEmployeeDetail(employee)}
                              >
                                <td className="p-3">
                                  <div>
                                    <p className="font-medium">{employee.firstName} {employee.lastName}</p>
                                    <p className="text-xs text-muted-foreground font-mono">{employee.employeeId}</p>
                                  </div>
                                </td>
                                <td className="p-3 text-sm">{employee.position || employee.jobTitle || '-'}</td>
                                <td className="p-3">
                                  <div className="text-sm">
                                    {employee.email && (
                                      <p className="flex items-center gap-1 text-muted-foreground">
                                        <Mail className="w-3 h-3" />
                                        {employee.email}
                                      </p>
                                    )}
                                    {employee.phone && (
                                      <p className="flex items-center gap-1 text-muted-foreground">
                                        <Phone className="w-3 h-3" />
                                        {employee.phone}
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span className="inline-flex items-center gap-1 text-sm">
                                    <Package className="w-4 h-4 text-muted-foreground" />
                                    {employee._count?.assets || 0}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <span className={cn(
                                    "px-2 py-0.5 rounded-full text-xs font-medium",
                                    employee.isActive 
                                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
                                  )}>
                                    {employee.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end pt-4 border-t mt-4">
                    <Button variant="outline" onClick={closeEmployeesModal}>
                      Close
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
